import {useMemo} from "react";
import {useSelector} from "react-redux";
import {useFetchCurrentUserQuery, useFetchUsersQuery} from "../../store/features/Users/userService";
import {useStatusSubscription} from "../../hooks/useStatusSubscription";
import {selectUserId} from "../../store/features/Auth/authSlice";
import type {TeamMember} from "./teamFilters";

/**
 * Everyone in the tenant except the current user, merged with their live
 * status. Shared by the Team list and the Team page header so both count the
 * same people; the queries are RTK-cached, so the second caller costs nothing.
 */
export const useTeamMembers = () => {
    const userId = useSelector(selectUserId);

    const {data: currentUser, isLoading: isCurrentUserLoading} = useFetchCurrentUserQuery(userId);
    const {data: users = [], isLoading: isUsersLoading} = useFetchUsersQuery({});
    const userIds = useMemo(() => users.map(user => user.id), [users]);

    const {statuses, isLoading: isStatusesLoading} = useStatusSubscription({userIds});

    // Colleagues whose status has not arrived yet are still listed; with no
    // telco status they fall into Offline.
    const members = useMemo<TeamMember[]>(() => {
        return users.reduce((acc: TeamMember[], user) => {
            if (String(user.id) === String(currentUser?.userId)) return acc;
            const status = statuses.find(status => String(status.userId) === String(user.id));
            acc.push({
                ...status,
                userId: user.id,
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                position: user.position || '',
                email: user.email || '',
                avatarUrl: user.avatarUrl || '',
                userTimeZone: user.userTimeZone || '',
            } as TeamMember);
            return acc;
        }, []);
    }, [users, statuses, currentUser?.userId]);

    // The status query is skipped until the subscriptions register, so "not
    // loading" can still mean "no statuses yet"; counts must wait for real data.
    const hasStatuses = statuses.length > 0;

    return {members, hasStatuses, isLoading: isUsersLoading || isStatusesLoading || isCurrentUserLoading};
};
